import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";
import { Link, useParams } from "react-router";

import { ReadOnlyAIAgentThreadProvider } from "@/Admin/ExploreAgentThreads/ReadOnlyAIAgentThreadProvider";
import { AgentThread } from "@/AgentThread/AgentThreadSidebar/AgentThread";
import { AgentThreadByIdView } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView";
import { AIAgentThreadProvider } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { NavigationContext, useNavigation } from "@/providers";
import { useAuth } from "@/providers/auth/hooks";
import { PageBreadcrumbs, PageLayout } from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useAgent, useAgentThread } from "@/shared/domain/agents/hooks";
import { RobotoDomainException } from "@/shared/http";
import { Goto } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services";

import styles from "./AgentThreadDetail.module.css";
import { describeThreadAccess } from "./threadAccess";
import { ThreadHeaderBar } from "./ThreadHeaderBar";
import { useSidebarHandoffGoto } from "./useSidebarHandoffGoto";

/**
 * Detail page for an agent thread at ``/agents/threads/:threadId``.
 *
 * Mirrors :class:`AdminAgentThreadViewer` for non-admin users. Branches on
 * ownership inside :func:`AgentThreadDetailContent`:
 *
 * - **Owner** (``agentThread.created_by`` matches the signed-in user): render
 *   the interactive ``<AgentThread />`` so the user can continue the
 *   conversation.
 * - **Non-owner** (a session visible to the user but created by a
 *   teammate): render ``<AgentThreadByIdView />`` inside
 *   ``ReadOnlyAIAgentThreadProvider``. Writes are no-ops; the user can fork
 *   from any assistant message to land on their own copy.
 * - **Slack-origin** (``origin === "slack"``): always read-only here, whoever
 *   is looking. The conversation lives in a Slack thread and only Slack may
 *   append to it (the API refuses with ``RobotoThreadReadOnlyException``);
 *   forking is the way to continue it in Roboto. Such threads are owned by
 *   the org's Slack service user, so in practice they land in the non-owner
 *   branch anyway — the origin check makes that a rule rather than a
 *   coincidence, and picks the banner copy.
 *
 * Loading and error states flow through ``<React.Suspense>`` and
 * ``<ErrorBoundary>`` rather than inline ``isLoading`` / ``error``
 * branching. The thread fetch lives inside :func:`AgentThreadDetailContent`
 * (and :func:`LoadedAgentThreadBreadcrumb` for the breadcrumb), both
 * of which call :func:`useAgentThread` and suspend until the response
 * is available. A 4xx response surfaces the server's message; anything
 * else falls back to a generic copy so we don't leak server-side
 * details.
 */
export function AgentThreadDetail() {
  const { threadId } = useParams<{ threadId: string }>();

  if (threadId === undefined) {
    // Programming error — the route declaration registers
    // ``/agents/threads/:threadId`` so React Router guarantees the
    // param is set by the time this component mounts.
    throw new Error("threadId not provided");
  }

  return (
    <PageLayout
      title={`Roboto - Agent thread - ${threadId}`}
      headerContentLeft={
        // The static trail backs both fallbacks: while the thread loads
        // (Suspense) and when the fetch fails (ErrorBoundary). The
        // boundary is load-bearing — the breadcrumb suspends on the same
        // thread fetch as the content area, and without it a rejection
        // would escape past the page to the router's boundary,
        // unmounting the whole route and bypassing the content area's
        // ``AgentThreadErrorAlert``.
        <ErrorBoundary
          fallback={() => <AgentThreadBreadcrumb threadId={threadId} />}
        >
          <React.Suspense
            fallback={<AgentThreadBreadcrumb threadId={threadId} />}
          >
            <LoadedAgentThreadBreadcrumb threadId={threadId} />
          </React.Suspense>
        </ErrorBoundary>
      }
    >
      <ErrorBoundary
        fallback={({ error }) => <AgentThreadErrorAlert error={error} />}
      >
        <React.Suspense fallback={<AgentThreadLoading />}>
          <AgentThreadDetailContent threadId={threadId} />
        </React.Suspense>
      </ErrorBoundary>
    </PageLayout>
  );
}

/**
 * Static trail (list link + thread id), used as the Suspense fallback.
 * Issues no network request, so the breadcrumb renders immediately
 * instead of flashing empty while the thread loads.
 */
function AgentThreadBreadcrumb({ threadId }: { threadId: string }) {
  return (
    <PageBreadcrumbs>
      <Link to={Goto.route.AgentThreads}>Agent Threads</Link>
      <span>{threadId}</span>
    </PageBreadcrumbs>
  );
}

/**
 * Loaded trail: suspends on :func:`useAgentThread`, then renders the
 * thread title as the final crumb (falling back to the thread id for an
 * untitled thread) and inserts a crumb linking to the agent the thread
 * was launched from (``created_from_agent_id``).
 *
 * ``useAgent`` is a plain query rather than a suspense hook, so the
 * agent crumb pops in when the lookup lands instead of blocking the
 * rest of the trail. While it loads — or when the agent no longer
 * resolves (deleted; the crumb would be a dead link) — the trail
 * keeps the agentless shape.
 */
function LoadedAgentThreadBreadcrumb({ threadId }: { threadId: string }) {
  const { data: thread } = useAgentThread({ threadId });
  const { currentOrganization } = useAuth();
  const { data: agent } = useAgent(thread.created_from_agent_id ?? undefined, {
    orgId: currentOrganization?.org_id,
  });

  return (
    <PageBreadcrumbs>
      <Link to={Goto.route.AgentThreads}>Agent Threads</Link>
      {agent !== undefined && (
        <Link to={Goto.createAgentRoute(agent.agent_id)}>{agent.name}</Link>
      )}
      <span>{thread.title ?? threadId}</span>
    </PageBreadcrumbs>
  );
}

function AgentThreadLoading() {
  return (
    <div className={styles.loadingContainer}>
      <CircularProgress size="1.5rem" />
    </div>
  );
}

function AgentThreadErrorAlert({ error }: { error: unknown }) {
  // 4xx errors are user-actionable (session not found, forbidden, etc.)
  // — surface the server's message verbatim. Anything else (5xx,
  // network failures, framework errors) gets the generic copy so we
  // don't leak server-side details and the user has a contact path.
  if (error instanceof RobotoDomainException && error.isClientError()) {
    return <Alert severity="error">{error.message}</Alert>;
  }
  return (
    <Alert severity="error">
      Failed to load session. Please contact{" "}
      <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this problem
      persists.
    </Alert>
  );
}

function AgentThreadDetailContent({ threadId }: { threadId: string }) {
  const { goto } = useNavigation();
  const { currentUser } = useAuth();
  const { data: thread } = useAgentThread({ threadId });
  const { goto: chatGoto, handedOff } = useSidebarHandoffGoto(threadId);
  const chatNavigation = React.useMemo(() => ({ goto: chatGoto }), [chatGoto]);

  useRegisterViewingContext("thread", { orgId: thread?.org_id });

  // Hold the owner/non-owner decision until the signed-in user is
  // loaded. ``currentUser`` is ``null`` while authentication is still
  // resolving, so checking ``thread.created_by`` against it too early
  // would briefly flash the read-only viewer on the user's own
  // thread.
  const ownerCheckResolved = currentUser !== null;
  const access = ownerCheckResolved
    ? describeThreadAccess(thread, currentUser.user_id)
    : null;
  const isOwnThread = access?.canContinue ?? false;

  // Keep the URL in sync when the active thread id changes from inside
  // the interactive branch — e.g. when the user starts a brand-new
  // conversation from this page instead of continuing the loaded
  // thread. Without this, reload and the browser back button would
  // point at the previous thread.
  const handleThreadIdChanged = React.useCallback(
    (nextId: string | undefined) => {
      if (nextId && nextId !== thread.thread_id) {
        goto.agentThread({ threadId: nextId });
      }
    },
    [thread.thread_id, goto],
  );

  // When the user forks a read-only thread, navigate this page to the
  // fork's detail URL so they land on their own copy and can continue
  // the conversation there.
  const handleForkNavigated = React.useCallback(
    (nextId: string) => {
      goto.agentThread({ threadId: nextId });
    },
    [goto],
  );

  return (
    <div className={styles.container}>
      <ThreadHeaderBar thread={thread} />

      <div className={styles.agentThreadContent}>
        {ownerCheckResolved && isOwnThread && (
          // No ``key``: ``threadId`` is live, so navigating between the user's
          // own threads swaps the conversation without tearing down the
          // client-tool executor mid-batch. Per-thread view state resets one
          // level down, where ``AgentThread`` keys the conversation view.
          <AIAgentThreadProvider
            threadId={thread.thread_id}
            onThreadIdChanged={handleThreadIdChanged}
          >
            {/* Only the conversation navigates through the handoff, so the
                agent's tool batch survives leaving this page. The breadcrumb
                and the nav rail sit outside and stay plain exits. */}
            <NavigationContext.Provider value={chatNavigation}>
              <AgentThread clientToolsSuspended={handedOff} />
            </NavigationContext.Provider>
          </AIAgentThreadProvider>
        )}
        {ownerCheckResolved && !isOwnThread && (
          <ReadOnlyAIAgentThreadProvider
            onAgentThreadClicked={handleForkNavigated}
          >
            <AgentThreadByIdView agentThread={thread} />
          </ReadOnlyAIAgentThreadProvider>
        )}
      </div>

      {access?.readOnlyNotice && (
        <div className={styles.readOnlyBanner}>{access.readOnlyNotice}</div>
      )}
    </div>
  );
}
